import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { X, AlertTriangle, History, Save, PencilLine, UserCheck } from 'lucide-react';
import {
  attendanceAdminAPI,
  ManualStatus,
  MonthlyGridDay,
} from '../../api/staffAttendanceManagement';

export const to12h = (hhmm?: string | null): string => {
  if (!hhmm) return '—';
  const m = String(hhmm).match(/(\d{1,2}):(\d{2})/);
  if (!m) return String(hhmm);
  let h = Number(m[1]);
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m[2]} ${ampm}`;
};

/** 'YYYY-MM-DD HH:mm:ss' → 'HH:mm' */
const dtToHHmm = (dt?: string | null): string => {
  if (!dt) return '';
  const m = String(dt).match(/(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : '';
};

const STATUS_OPTIONS: { value: ManualStatus; label: string; cls: string }[] = [
  { value: 'present', label: 'Present', cls: 'bg-emerald-600' },
  { value: 'half_day', label: 'Half-Day', cls: 'bg-cyan-600' },
  { value: 'leave', label: 'Leave', cls: 'bg-indigo-600' },
  { value: 'absent', label: 'Absent', cls: 'bg-rose-600' },
];

interface Props {
  mode: 'create' | 'edit';
  staffOptions: { id: number; full_name: string; staff_code?: string }[];
  initialStaffId?: number | null;
  initialDate?: string;
  dayItem?: MonthlyGridDay | null;
  staffName?: string;
  salaryPaid?: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export default function AttendanceEntryModal({
  mode, staffOptions, initialStaffId, initialDate, dayItem, staffName, salaryPaid, onClose, onSaved,
}: Props) {
  const initialStatus: ManualStatus = useMemo(() => {
    const raw = dayItem?.raw_status;
    if (raw === 'late' || raw === 'present') return 'present';
    if (raw === 'half_day' || raw === 'leave' || raw === 'absent') return raw;
    return 'present';
  }, [dayItem]);

  const [staffId, setStaffId] = useState<number | ''>(initialStaffId ?? '');
  const [date, setDate] = useState<string>(initialDate || new Date().toISOString().slice(0, 10));
  const [status, setStatus] = useState<ManualStatus>(initialStatus);
  const [checkIn, setCheckIn] = useState<string>(dtToHHmm(dayItem?.check_in_time) || (mode === 'create' ? '10:00' : ''));
  const [checkOut, setCheckOut] = useState<string>(dtToHHmm(dayItem?.check_out_time));
  const [leavePaid, setLeavePaid] = useState<boolean>(dayItem?.leave_paid === true);
  const [reason, setReason] = useState('');

  const attendanceId = dayItem?.attendance_id ?? null;
  const needsTimes = status === 'present' || status === 'half_day';

  const { data: historyRes } = useQuery({
    queryKey: ['attendanceEditHistory', attendanceId],
    queryFn: () => attendanceAdminAPI.getRecordHistory(attendanceId as number),
    enabled: mode === 'edit' && !!attendanceId,
  });
  const history = historyRes?.data || [];

  const mutation = useMutation({
    mutationFn: async () => {
      const base = {
        status,
        check_in: needsTimes ? (checkIn || null) : null,
        check_out: needsTimes ? (checkOut || null) : null,
        leave_paid: status === 'leave' ? leavePaid : null,
        reason: reason.trim(),
      };
      if (mode === 'edit' && attendanceId) return attendanceAdminAPI.editRecord(attendanceId, base);
      return attendanceAdminAPI.markManual({ ...base, staff_id: Number(staffId), date });
    },
    onSuccess: (res: any) => {
      if (res?.data?.salary_already_paid) {
        toast(res.message || 'Saved. Salary for this month is already paid — recorded payment will not change.', { icon: '⚠️', duration: 6000 });
      } else {
        toast.success(res?.message || 'Attendance saved.');
      }
      onSaved();
      onClose();
    },
    onError: (err: any) => {
      const e = err?.response?.data?.error;
      const detail = e?.details?.[0]?.message;
      toast.error(detail || e?.message || 'Failed to save attendance.');
    },
  });

  const handleSubmit = () => {
    if (mode === 'create' && !staffId) { toast.error('Please select a staff member.'); return; }
    if (needsTimes && !checkIn) { toast.error('Check-in time is required.'); return; }
    if (status === 'half_day' && !checkOut) { toast.error('Check-out time is required for Half-Day.'); return; }
    if (needsTimes && checkIn && checkOut && checkOut < checkIn) { toast.error('Check-out must be after check-in.'); return; }
    if (reason.trim().length < 3) { toast.error('Please enter a reason (min 3 characters).'); return; }
    mutation.mutate();
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md border border-gray-300 w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 sticky top-0">
          <div className="flex items-center space-x-2 font-black text-black text-sm">
            {mode === 'edit' ? <PencilLine className="w-4 h-4 text-red-600" /> : <UserCheck className="w-4 h-4 text-red-600" />}
            <span>{mode === 'edit' ? 'Edit Attendance' : 'Mark Attendance Manually'}</span>
          </div>
          <button id="attendance-entry-close" onClick={onClose} className="p-1 hover:bg-gray-200 rounded cursor-pointer">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-3.5 text-xs">
          {salaryPaid && (
            <div className="flex items-start space-x-2 p-2.5 bg-amber-50 border border-amber-300 rounded text-amber-900 font-bold">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Salary for this month is already marked as PAID. You can still edit, but the recorded payment amount will NOT change.</span>
            </div>
          )}

          {mode === 'create' ? (
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="font-black text-gray-800 block mb-1">Staff Member *</span>
                <select
                  id="attendance-entry-staff"
                  value={staffId}
                  onChange={(e) => setStaffId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-2.5 py-2 border border-gray-300 rounded font-bold text-black focus:outline-none focus:border-red-600"
                >
                  <option value="">Select staff...</option>
                  {staffOptions.map((s) => (
                    <option key={s.id} value={s.id}>{s.full_name}{s.staff_code ? ` (${s.staff_code})` : ''}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="font-black text-gray-800 block mb-1">Date *</span>
                <input
                  id="attendance-entry-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-2.5 py-2 border border-gray-300 rounded font-bold text-black focus:outline-none focus:border-red-600"
                />
              </label>
            </div>
          ) : (
            <div className="p-2.5 bg-gray-50 border border-gray-200 rounded font-bold text-gray-800 flex flex-wrap gap-x-4 gap-y-1">
              <span>Staff: <span className="text-black font-black">{staffName}</span></span>
              <span>Date: <span className="text-black font-black font-mono">{initialDate}</span></span>
              {dayItem?.is_manual && <span className="text-indigo-700">Manual entry</span>}
              {dayItem?.is_edited && <span className="text-purple-700">Edited by Admin</span>}
            </div>
          )}

          <div>
            <span className="font-black text-gray-800 block mb-1">Status *</span>
            <div className="grid grid-cols-4 gap-1.5">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  id={`attendance-entry-status-${opt.value}`}
                  type="button"
                  onClick={() => setStatus(opt.value)}
                  className={`py-2 rounded font-black border transition-all cursor-pointer ${
                    status === opt.value ? `${opt.cls} text-white border-transparent` : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {status === 'present' && (
              <p className="mt-1 text-[11px] text-gray-600 font-semibold">Late badge is decided automatically from the staff shift + grace period.</p>
            )}
          </div>

          {needsTimes && (
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="font-black text-gray-800 block mb-1">Check-in Time *</span>
                <input
                  id="attendance-entry-checkin"
                  type="time"
                  value={checkIn}
                  onChange={(e) => setCheckIn(e.target.value)}
                  className="w-full px-2.5 py-2 border border-gray-300 rounded font-bold text-black focus:outline-none focus:border-red-600"
                />
                <span className="text-[11px] text-gray-600 font-bold">{to12h(checkIn)}</span>
              </label>
              <label className="block">
                <span className="font-black text-gray-800 block mb-1">
                  Check-out Time {status === 'half_day' ? '*' : '(optional)'}
                </span>
                <input
                  id="attendance-entry-checkout"
                  type="time"
                  value={checkOut}
                  onChange={(e) => setCheckOut(e.target.value)}
                  className="w-full px-2.5 py-2 border border-gray-300 rounded font-bold text-black focus:outline-none focus:border-red-600"
                />
                <span className="text-[11px] text-gray-600 font-bold">
                  {checkOut ? to12h(checkOut) : 'Not checked out'}
                  {checkOut && (
                    <button type="button" onClick={() => setCheckOut('')} className="ml-2 text-red-600 underline cursor-pointer">clear</button>
                  )}
                </span>
              </label>
            </div>
          )}

          {status === 'leave' && (
            <div>
              <span className="font-black text-gray-800 block mb-1">Leave Type *</span>
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  id="attendance-entry-leave-paid"
                  type="button"
                  onClick={() => setLeavePaid(true)}
                  className={`py-2 rounded font-black border cursor-pointer ${leavePaid ? 'bg-emerald-600 text-white border-transparent' : 'bg-white text-gray-700 border-gray-300'}`}
                >
                  Paid Leave (no cut)
                </button>
                <button
                  id="attendance-entry-leave-unpaid"
                  type="button"
                  onClick={() => setLeavePaid(false)}
                  className={`py-2 rounded font-black border cursor-pointer ${!leavePaid ? 'bg-rose-600 text-white border-transparent' : 'bg-white text-gray-700 border-gray-300'}`}
                >
                  Unpaid Leave (salary cut)
                </button>
              </div>
            </div>
          )}

          <label className="block">
            <span className="font-black text-gray-800 block mb-1">Reason *</span>
            <textarea
              id="attendance-entry-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="e.g. Staff forgot to check in, was present from 10 AM"
              className="w-full px-2.5 py-2 border border-gray-300 rounded font-semibold text-black focus:outline-none focus:border-red-600"
            />
          </label>

          {mode === 'edit' && history.length > 0 && (
            <div className="border border-gray-200 rounded">
              <div className="px-2.5 py-1.5 bg-gray-50 border-b border-gray-200 font-black text-gray-800 flex items-center space-x-1.5">
                <History className="w-3.5 h-3.5" /><span>Edit History ({history.length})</span>
              </div>
              <div className="divide-y divide-gray-100 max-h-40 overflow-y-auto">
                {history.map((h) => (
                  <div key={h.id} className="px-2.5 py-1.5 text-[11px] font-semibold text-gray-700">
                    <div className="flex justify-between">
                      <span className="font-black text-black">{h.action === 'manual_create' ? 'Created manually' : 'Edited'} by {h.edited_by_name || 'Admin'}</span>
                      <span className="font-mono text-gray-500">{String(h.created_at).slice(0, 16)}</span>
                    </div>
                    {h.action === 'edit' && (
                      <div>
                        {h.old_status} → {h.new_status} · In {to12h(dtToHHmm(h.old_check_in_time))} → {to12h(dtToHHmm(h.new_check_in_time))} · Out {to12h(dtToHHmm(h.old_check_out_time))} → {to12h(dtToHHmm(h.new_check_out_time))}
                      </div>
                    )}
                    <div className="italic">“{h.reason}”</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-200 flex justify-end space-x-2 bg-gray-50 sticky bottom-0">
          <button onClick={onClose} className="px-3.5 py-2 bg-white border border-gray-300 rounded text-xs font-black cursor-pointer hover:bg-gray-100">
            Cancel
          </button>
          <button
            id="attendance-entry-save"
            onClick={handleSubmit}
            disabled={mutation.isPending}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white rounded text-xs font-black flex items-center space-x-1.5 cursor-pointer"
          >
            <Save className="w-3.5 h-3.5" />
            <span>{mutation.isPending ? 'Saving...' : 'Save Attendance'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
